import * as React from 'react';
import { PinnacleTabs } from '../../../../../components/PinnacleTabs/PinnacleTabs';
import { Box } from '@mui/material';
import { pushNotification } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../../../../utils/errorHandler';
import ExamInformation from './tabs/ExamInformation';
import QuestionnaireUploadForm from './tabs/QuestionnaireUploadForm';
import AnswersKeyForm from './tabs/AnswersKeyForm';
import { ExamByCodeFormData } from '../../../hooks/useExamByCodeForm';

interface EditExamByCodeDrawerContentProps {
  formData: ExamByCodeFormData;
  rawFormData: ExamByCodeFormData;
  touchedFields: Record<keyof ExamByCodeFormData, boolean>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (field: keyof ExamByCodeFormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  isFormValid: () => boolean;
  
  handleUploadQuestionnaireClick: () => void;
  fileQuestionnaireInputRef: React.RefObject<HTMLInputElement>;
  handleFileQuestionnaireChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedQuestionnaireFile: File | null | any;
  
  handleUploadQuestionnaireKeysClick: () => void;
  fileQuestionnaireKeysInputRef: React.RefObject<HTMLInputElement>;
  handleFileQuestionnaireKeysChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedQuestionnaireKeysFile: File | null | any;

  closeToggle: () => void;
}

export interface EditExamByCodeDrawerContentHandle {
  handleSubmit: () => void;
}

const EditExamByCodeDrawerContent = React.forwardRef<EditExamByCodeDrawerContentHandle, EditExamByCodeDrawerContentProps>(
  ({
    formData,
    rawFormData,
    touchedFields,
    onChange,
    onBlur,
    onSubmit,
    isSubmitting,
    isFormValid,
    handleUploadQuestionnaireClick,
    fileQuestionnaireInputRef,
    handleFileQuestionnaireChange,
    selectedQuestionnaireFile,
    handleUploadQuestionnaireKeysClick,
    fileQuestionnaireKeysInputRef,
    handleFileQuestionnaireKeysChange,
    selectedQuestionnaireKeysFile,
    setIsSubmitting,
  }, ref) => {
    React.useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        setIsSubmitting(true);

        try {
          if (isFormValid()) {
            await onSubmit();
          } else {
            pushNotification({
              details: 'Some required fields are missing.',
              type: NotificationTypes.Error,
            });
          }
        } catch (error) {
          handleApiError(error, {
            notify: (msg) => {
              pushNotification({
                details: msg,
                type: NotificationTypes.Error,
              });
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    }));

    return (
      <Box display="flex" flexDirection="column" height="100%">
        <PinnacleTabs
          disabled={isSubmitting}
          tabs={[
            {
              label: "Exam Information",
              content: (
                <ExamInformation
                  formData={rawFormData}
                  touchedFields={touchedFields}
                  onChange={onChange}
                  onBlur={onBlur}
                  isSubmitting={isSubmitting}
                />
              ),
            },
            {
              label: "Questionnaire",
              content: (
                <QuestionnaireUploadForm
                  formData={rawFormData}
                  touchedFields={touchedFields}
                  onChange={onChange}
                  onBlur={onBlur}
                  isSubmitting={isSubmitting}
                  handleUploadQuestionnaireClick={handleUploadQuestionnaireClick}
                  fileQuestionnaireInputRef={fileQuestionnaireInputRef}
                  handleFileQuestionnaireChange={handleFileQuestionnaireChange}
                  selectedQuestionnaireFile={selectedQuestionnaireFile}
                  handleUploadQuestionnaireKeysClick={handleUploadQuestionnaireKeysClick}
                  fileQuestionnaireKeysInputRef={fileQuestionnaireKeysInputRef}
                  handleFileQuestionnaireKeysChange={handleFileQuestionnaireKeysChange}
                  selectedQuestionnaireKeysFile={selectedQuestionnaireKeysFile}
                />
              ),
            },
            {
              label: "Answer key",
              content: (
                <AnswersKeyForm
                  formData={rawFormData}
                  touchedFields={touchedFields}
                  onChange={onChange}
                  onBlur={onBlur}
                  isSubmitting={isSubmitting}
                />
              ),
              disabledTabItem: false
            },
          ]}
        />
      </Box>
    );
  }
);


export default EditExamByCodeDrawerContent;
